import React, { useState, useEffect } from "react";
import SwoodleUpdatedLogo from "../../../Assets/Icons/SwoodleUpdatedLogo.svg";
import PrimaryButton from "../../common/PrimaryButton";
import TextInput from "../../common/SignUp/TextInput";
import person from "../../../Assets/Images/Person.svg";
import passwordIcon from "../../../Assets/Icons/passwordIcon.svg";
import abnIcon from "../../../Assets/Icons/ABN icon.svg";
import charityIcon from "../../../Assets/Icons/Charity icon.svg";
import emailIcon from "../../../Assets/Icons/Email icon.svg";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { createFundraiserAccountSchema } from "../../../utils/validationSchemas";
import { useNavigate, useParams } from "react-router-dom";
import {
  fundraiserOnboard,
  getFundraiserInviteByCode,
} from "../../../services/onboardingService";
import Loader from "../../common/Loader";

const defaultInitialValues = {
  contactPersonName: "",
  password: "",
  confirmPassword: "",
  abn: "",
  isRegisteredCharity: false,
  isRegisteredForGST: false,
  charityRegistrationNumber: "",
  accountEmail: "",
};

const CreateAccount = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setConfirmShowPassword] = useState(false);
  const [initialValues, setInitialValues] = useState(defaultInitialValues);
  const { verificationCode } = useParams();
  const loading = useSelector((state) => state?.onboardingReducer?.loading);

  // Only check presence of verificationCode and redirect if missing
  useEffect(() => {
    if (!verificationCode) {
      navigate("/");
    }
  }, [verificationCode, navigate]);

  useEffect(() => {
    if (!verificationCode) return;

    let isMounted = true;

    const loadInvitationDetails = async () => {
      try {
        const response = await dispatch(
          getFundraiserInviteByCode(verificationCode),
        ).unwrap();

        if (!isMounted || response?.success !== true) return;

        setInitialValues((prev) => ({
          ...prev,
          contactPersonName: response?.data?.contactPersonName || "",
          accountEmail: response?.data?.accountEmail || "",
        }));
      } catch (_) {}
    };

    loadInvitationDetails();

    return () => {
      isMounted = false;
    };
  }, [dispatch, verificationCode]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: createFundraiserAccountSchema,
    onSubmit: async (values) => {
      try {
        const merchantPromise = await dispatch(
          fundraiserOnboard({
            password: values?.password,
            code: verificationCode, // <-- Using verificationCode from URL
            abn: values?.abn,
            isRegisteredToGST: values?.isRegisteredForGST,
            isRegisteredCharity: values?.isRegisteredCharity,
            charityRegistrationNumber: values?.charityRegistrationNumber,
            accountEmail: values?.accountEmail, // Send the accountEmail field instead of displayEmail
            contactPersonName: values?.contactPersonName,
          }),
        ).unwrap();
        if (merchantPromise?.success === true) {
          // Fire GTM event for fundraiser onboarding success
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "fundraiser_form_completed",
            formType: "Fundraiser",
          });
          navigate("/fundraiser/account-created", {
            state: { verificationCode },
          });
        }
      } catch (error) {
        throw error;
      }
    },
  });

  const {
    contactPersonName,
    password,
    confirmPassword,
    abn,
    charityRegistrationNumber,
    accountEmail,
    isRegisteredCharity,
    isRegisteredForGST,
  } = formik.values;

  const {
    handleChange,
    handleBlur,
    handleSubmit,
    errors,
    touched,
    setFieldValue,
  } = formik;

  // Custom handler for ABN field to limit to 11 digits only
  const handleAbnChange = (e) => {
    const value = e.target.value;
    // Only allow digits and limit to 11 characters
    const digitsOnly = value.replace(/\D/g, "").slice(0, 11);
    setFieldValue("abn", digitsOnly);
  };

  // Only allow digits for Charity Registration Number
  const handleCharityRegistrationChange = (e) => {
    const value = e.target.value;
    const digitsOnly = value.replace(/\D/g, "");
    setFieldValue("charityRegistrationNumber", digitsOnly);
  };

  const scrollToFirstError = (formErrors) => {
    const priority = [
      "contactPersonName",
      "password",
      "confirmPassword",
      "abn",
      "accountEmail",
      "charityRegistrationNumber",
    ];
    let targetField = priority.find(
      (key) =>
        formErrors && Object.prototype.hasOwnProperty.call(formErrors, key),
    );
    if (!targetField) {
      const keys = Object.keys(formErrors || {});
      targetField = keys.length ? keys[0] : null;
    }
    if (!targetField) return;
    const el =
      document.querySelector(`[data-field="${targetField}"]`) ||
      document.querySelector(`[name="${targetField}"]`);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof el.focus === "function") {
        try {
          el.focus();
        } catch (_) {}
      }
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = await formik.validateForm();
    if (Object.keys(validationErrors || {}).length > 0) {
      formik.setTouched(
        Object.keys(validationErrors).reduce((acc, k) => {
          acc[k] = true;
          return acc;
        }, {}),
        true,
      );
      scrollToFirstError(validationErrors);
      return;
    }
    handleSubmit(e);
  };

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPassword = () => {
    setConfirmShowPassword(!showConfirmPassword);
  };

  return (
    <form
      className="w-full py-10 h-screen flex flex-col items-center offerform"
      onSubmit={handleFormSubmit}
    >
      {loading && <Loader />}
      <>
        <p className="font-poppins font-[500] text-[14px] sm:text-[18px] md:text-[22px] lg:text-[26px] text-[#303030] px-4">
          Welcome! Please Complete Your Setup
        </p>
        <div className="flex flex-col items-center w-[90%] sm:w-[70%] md:w-[60%] lg:w-[50%] xl:w-[45%] 2xl:w-[40%]">
          <img
            src={SwoodleUpdatedLogo}
            alt="Swoodle"
            className="pt-[6%] pb-[2%] w-[60%]"
          ></img>
            <p className="font-poppins font-[500] sm:text-[18px] md:text-[22px] lg:text-[26px] text-[#303030]">
            FUNDRAISER
          </p>
          <div className="flex flex-col justify-start items-start gap-2 w-full">
            <p className="font-poppins font-[500] text-[14px] sm:text-[16px] md:text-[18px] lg:text-[18px] xl:text-[22px] text-[#303030] mt-6 sm:mt-10 text-start">
              Set Up Your Account
            </p>
            <TextInput
              Icon={person}
              name={"contactPersonName"}
              value={contactPersonName}
              placeholder={"Fundraiser Contact Name"}
              inputClass={
                "w-full rounded-tr-[5px] rounded-br-[5px] border border-[#D9D9D9] pl-[2%]"
              }
              iconAreaClass={
                "w-[10%] bg-[#E6E6E6] rounded-tl-[5px] rounded-bl-[5px] border border-[#D9D9D9]"
              }
              innerDivClass={"h-[45px] sm:h-[55px]"}
              inputDivClass={"h-[70px] sm:h-[75px]"}
              handleChange={handleChange}
              handleBlur={handleBlur}
              error={errors.contactPersonName}
              touched={touched.contactPersonName}
            />

            <TextInput
              Icon={passwordIcon}
              type={showPassword ? "text" : "password"}
              name={"password"}
              data-field="password"
              value={password}
              placeholder={"Fundraiser Password"}
              inputClass={
                "w-full rounded-tr-[5px] rounded-br-[5px] border border-[#D9D9D9] pl-[2%]"
              }
              iconAreaClass={
                "w-[10%] bg-[#E6E6E6] rounded-tl-[5px] rounded-bl-[5px] border border-[#D9D9D9]"
              }
              innerDivClass={"h-[45px] sm:h-[55px]"}
              inputDivClass={"h-[70px] sm:h-[75px]"}
              handleChange={handleChange}
              handleBlur={handleBlur}
              error={errors.password}
              touched={touched.password}
              showPassword={showPassword}
              togglePassword={togglePassword}
              autoComplete={"new-password"}
            />
            <TextInput
              Icon={passwordIcon}
              type={showConfirmPassword ? "text" : "password"}
              name={"confirmPassword"}
              data-field="confirmPassword"
              value={confirmPassword}
              placeholder={"Confirm Password"}
              inputClass={
                "w-full rounded-tr-[5px] rounded-br-[5px] border border-[#D9D9D9] pl-[2%]"
              }
              iconAreaClass={
                "w-[10%] bg-[#E6E6E6] rounded-tl-[5px] rounded-bl-[5px] border border-[#D9D9D9]"
              }
              innerDivClass={"h-[45px] sm:h-[55px]"}
              inputDivClass={"h-[70px] sm:h-[75px]"}
              handleChange={handleChange}
              handleBlur={handleBlur}
              error={errors.confirmPassword}
              touched={touched.confirmPassword}
              showPassword={showConfirmPassword}
              togglePassword={toggleConfirmPassword}
            />
            <TextInput
              Icon={abnIcon}
              name={"abn"}
              data-field="abn"
              value={abn}
              placeholder={"Fundraiser ABN"}
              inputClass={
                "w-full rounded-tr-[5px] rounded-br-[5px] border border-[#D9D9D9] pl-[2%]"
              }
              iconAreaClass={
                "w-[10%] sm:w-[10%] bg-[#E6E6E6] rounded-tl-[5px] rounded-bl-[5px] border border-[#D9D9D9] flex items-center justify-center"
              }
              innerDivClass={"h-[45px] sm:h-[55px]"}
              inputDivClass={"h-[70px] sm:h-[75px]"}
              handleChange={handleAbnChange}
              handleBlur={handleBlur}
              error={errors.abn}
              touched={touched.abn}
              maxLength={11}
            />

            {/* Charity Registration Checkbox */}
            <div className="flex flex-row items-center gap-8 mt-2 mb-6">
              <div className="flex items-center gap-2">
                <label className="relative flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    id="charityRegistration"
                    checked={isRegisteredCharity}
                    onChange={(e) =>
                      setFieldValue("isRegisteredCharity", e.target.checked)
                    }
                    className="peer appearance-none w-4 h-4 border border-gray-300 rounded bg-white checked:bg-[#00ADEF] focus:ring-2 focus:ring-[#00ADEF]"
                  />
                  <svg
                    className="pointer-events-none absolute left-0 top-0 w-4 h-4 hidden peer-checked:block"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M4 8.5L7 11.5L12 6.5"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </label>
                <label
                  htmlFor="charityRegistration"
                  className="text-[14px] font-poppins font-[400] text-[#303030] ml-2"
                >
                  Are you a Registered Charity?
                </label>
              </div>
              {/* GST Registration Checkbox */}
              <div className="flex items-center gap-2">
                <label className="relative flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    id="gstRegistration"
                    checked={isRegisteredForGST}
                    onChange={(e) =>
                      setFieldValue("isRegisteredForGST", e.target.checked)
                    }
                    className="peer appearance-none w-4 h-4 border border-gray-300 rounded bg-white checked:bg-[#00ADEF] focus:ring-2 focus:ring-[#00ADEF]"
                  />
                  <svg
                    className="pointer-events-none absolute left-0 top-0 w-4 h-4 hidden peer-checked:block"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M4 8.5L7 11.5L12 6.5"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </label>
                <label
                  htmlFor="gstRegistration"
                  className="text-[14px] font-poppins font-[400] text-[#303030] ml-2"
                >
                  Are you Registered for GST?
                </label>
              </div>
            </div>

            {/* Charity Registration Number - only show if charity checkbox is checked */}
            {isRegisteredCharity && (
              <TextInput
                Icon={charityIcon}
                name={"charityRegistrationNumber"}
                data-field="charityRegistrationNumber"
                value={charityRegistrationNumber}
                placeholder={"Charity Registration Number"}
                inputClass={
                  "w-full rounded-tr-[5px] rounded-br-[5px] border border-[#D9D9D9] pl-[2%]"
                }
                iconAreaClass={
                  "w-[10%] sm:w-[10%] bg-[#E6E6E6] rounded-tl-[5px] rounded-bl-[5px] border border-[#D9D9D9] flex items-center justify-center"
                }
                innerDivClass={"h-[45px] sm:h-[55px]"}
                inputDivClass={"h-[70px] sm:h-[75px]"}
                handleChange={handleCharityRegistrationChange}
                handleBlur={handleBlur}
                error={errors.charityRegistrationNumber}
                touched={touched.charityRegistrationNumber}
              />
            )}

            <TextInput
              Icon={emailIcon}
              name={"accountEmail"}
              data-field="accountEmail"
              value={accountEmail}
              placeholder={"Fundraiser Accounts Email"}
              inputClass={
                "w-full rounded-tr-[5px] rounded-br-[5px] border border-[#D9D9D9] pl-[2%]"
              }
              iconAreaClass={
                "w-[10%] sm:w-[10%] bg-[#E6E6E6] rounded-tl-[5px] rounded-bl-[5px] border border-[#D9D9D9] flex items-center justify-center"
              }
              innerDivClass={"h-[45px] sm:h-[55px]"}
              inputDivClass={"h-[70px] sm:h-[75px]"}
              handleChange={handleChange}
              handleBlur={handleBlur}
              error={errors.accountEmail}
              touched={touched.accountEmail}
            />

            <p className="text-[11px] sm:text-[12px] md:text-[14px] font-poppins font-[400] mt-2 text-start w-[95%] leading-tight">
              By clicking 'Create Account', I agree to the
              <span
                className="text-[#00ADEF] font-[500] cursor-pointer"
                onClick={() =>
                  window.open(
                    "https://swoodle.com.au/terms-and-conditions/",
                    "_blank",
                  )
                }
              >
                {" "}
                Terms of Use{" "}
              </span>
              that I have read the
              <span
                className="text-[#00ADEF] font-[500] cursor-pointer"
                onClick={() =>
                  window.open(
                    "https://swoodle.com.au/privacy-policy/",
                    "_blank",
                  )
                }
              >
                {" "}
                Privacy Policy.
              </span>
            </p>
          </div>
          <div className="text-center w-full" onClick={handleSubmit}>
            <PrimaryButton
              type={"submit"}
              className={
                "w-[85%] sm:w-[50%] md:w-[40%] lg:w-[50%] font-poppins text-[12px] sm:text-[14px] lg:text-[16px] text-center font-[500] h-[40px] sm:h-[45px] md:h-[50px] lg:h-[55px] rounded-[10px] my-6 sm:my-10 tracking-wide"
              }
              label="Create Account"
            />
          </div>
        </div>
      </>
    </form>
  );
};

export default CreateAccount;
